/**
 * F-170 (B4) — Cart REST client.
 *
 * Backend (B0.2 + B0.12): each cart is per (client, specialist) and resolves
 * to a single proposed `Order` + dedicated chat thread on checkout. The FE
 * keeps state minimal — read on render, mutate per action, refetch.
 */
import { api } from './client';
import type { Order } from '@/types/domain';

export type CartStatus = 'open' | 'checked_out' | 'cancelled';

export interface CartItem {
  id: number;
  product_id: number;
  product_title: string;
  product_image_url: string | null;
  qty: number;
  price_eur_at_add: string;
  line_total_eur: string;
  created_at: string;
}

export interface Cart {
  id: number;
  client_id: number;
  specialist_id: number;
  status: CartStatus;
  items: CartItem[];
  total_eur: string;
  item_count: number;
  created_at: string;
  checked_out_at: string | null;
}

export async function fetchOpenCarts(): Promise<{ results: Cart[] }> {
  return api.get('/carts/');
}

export async function fetchActiveCart(specialistId: string | number): Promise<{ cart: Cart | null }> {
  return api.get(`/carts/active/${specialistId}/`);
}

export async function openCart(specialist: {
  id?: number;
  slug?: string;
}): Promise<Cart> {
  return api.post('/carts/', {
    body: specialist.id != null ? { specialist_id: specialist.id } : { specialist_slug: specialist.slug },
  });
}

export async function fetchCart(cartId: number): Promise<Cart> {
  return api.get(`/carts/${cartId}/`);
}

export async function addItem(cartId: number, productId: number, qty = 1): Promise<CartItem> {
  return api.post(`/carts/${cartId}/items/`, { body: { product_id: productId, qty } });
}

export async function updateItem(cartId: number, itemId: number, qty: number): Promise<CartItem> {
  return api.patch(`/carts/${cartId}/items/${itemId}/`, { body: { qty } });
}

export async function removeItem(cartId: number, itemId: number): Promise<void> {
  await api.del(`/carts/${cartId}/items/${itemId}/`);
}

export type CartDeliveryMode = 'pickup' | 'delivery' | 'shipping';

export interface CheckoutInput {
  delivery_mode?: CartDeliveryMode;
  address_text?: string;
  comment?: string;
}

export interface CheckoutResponse {
  order: Order;
  chat_id: number;
}

export async function checkout(cartId: number, input: CheckoutInput): Promise<CheckoutResponse> {
  return api.post(`/carts/${cartId}/checkout/`, { body: input });
}
